import { z } from 'zod';
import { nullableText, paginationSchema } from './common.ts';

/**
 * Normalisation du nom — règle 12. Une seule implémentation, partagée par la
 * saisie manuelle, l'autocomplétion et l'import : trim, espaces internes
 * réduits, majuscules, accents supprimés. Le nom reste stocké tel que saisi.
 */
export function normaliserNom(nom: string): string {
  return nom
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

/** Normalisation du téléphone : on ne garde que les chiffres. */
export function normaliserTel(tel: string | null | undefined): string | null {
  if (!tel) return null;
  const chiffres = tel.replace(/\D/g, '');
  return chiffres === '' ? null : chiffres;
}

export const clientInputSchema = z.object({
  nom: z.string().trim().min(1, 'Le nom est obligatoire').max(200),
  tel: nullableText,
  ice: nullableText,
  note: nullableText,
});
export type ClientInput = z.infer<typeof clientInputSchema>;

export const clientUpdateSchema = clientInputSchema.partial();
export type ClientUpdate = z.infer<typeof clientUpdateSchema>;

export const clientListQuerySchema = paginationSchema.extend({
  q: z.string().trim().optional(),
  tri: z.enum(['nom', 'dernier_achat', 'total_depense', 'reste_du']).default('dernier_achat'),
  sens: z.enum(['asc', 'desc']).default('desc'),
});
export type ClientListQuery = z.infer<typeof clientListQuerySchema>;

export const fusionnerSchema = z.object({
  mergeIds: z.array(z.string().min(1)).min(1, 'Sélectionnez au moins un client à fusionner'),
});
export type FusionnerInput = z.infer<typeof fusionnerSchema>;

export type Client = {
  id: string;
  nom: string;
  tel: string | null;
  ice: string | null;
  note: string | null;
  created_at: string;
  updated_at: string | null;
};

/** Ligne de la liste /clients — agrégats calculés côté SQL. */
export type ClientAvecStats = Client & {
  nombre_achats: number;
  total_depense: number;
  reste_du: number;
  dernier_achat: string | null;
};
